/**
 * The non-destructive action palette. (PANDA-1779)
 *
 * Named for modals because that is where the audit started, and the names are
 * kept so the ~40 call sites stay greppable. It applies to any surface with
 * the same job — the settings cards adopted it too, since a user walks
 * straight from "Add credits" into Settings → Billing and the two reading as
 * different products was the thing this policy exists to stop.
 *
 * Panda's primary CTA has always been the brand gradient — `.bg-panda-gradient`,
 * #FF5606 → #D52A26 → #AA0346. That is orange *into crimson*, and for a long
 * time it sat on the primary button of almost every modal we ship: upgrade,
 * top-up, activate ads, resume ads, verify your email, restore this version.
 * Those are the moments we most want the user to move forward, and the button
 * asking them to was the same colour as the one that deletes their project.
 *
 * PANDA-1778 fixed one instance of this (the $1 trial modal) by adding the
 * `success` button variant. This module is the generalisation of that fix, and
 * exists because most modals in this codebase do NOT use `<Button>` — they
 * hand-roll a `<button className="bg-panda-gradient …">`. Without a shared
 * constant the conversion would be twenty near-identical class strings that
 * drift apart the first time someone tunes a hover state.
 *
 * ── When to use which colour ────────────────────────────────────────────────
 *
 *   GREEN (this module)  The action moves the user forward and can be undone
 *                        or simply repeated: upgrade, start a trial, buy
 *                        credits, activate/resume/retry, verify, publish,
 *                        restore, save, continue, submit feedback.
 *
 *   RED                  The action destroys something or cannot be walked
 *                        back: delete a project, remove a campaign, confirm a
 *                        subscription cancellation. Also genuine error and
 *                        validation states — a failed payment must read as a
 *                        failure. Use the `destructive` button variant.
 *
 *   AMBER                Warnings and disclosures the user is legally or
 *                        practically required to notice: the trial's renewal
 *                        charge (DMCC/FTC), a budget that will stop spending,
 *                        an account that needs attention. Amber must stay
 *                        alarming; do not "soften" a disclosure to green.
 *
 *   BRAND ORANGE         Identity, not action. The Panda logo mark, plan-tier
 *                        icons, the leaderboard's flames and podium medals.
 *                        These are not CTAs and nobody reads them as danger,
 *                        so they keep the brand hue. Cancellation/downgrade
 *                        flows also keep it: those steps are neither
 *                        encouraged nor destructive, and painting "Continue to
 *                        downgrade" green would be a dark pattern.
 *
 *                        NARROWED BY PANDA-2025. This clause used to say "the
 *                        streak flame", and that exemption was read as covering
 *                        the whole streak surface — so the dashboard's streak
 *                        card wore the brand gradient on its medallion AND its
 *                        milestone progress bar, which is a progress bar for an
 *                        encouraged habit painted the colour of failure. The
 *                        streak CARD and the header CHIP are green now; only
 *                        the leaderboard modal keeps warm flames, where they
 *                        are a heat/ranking metaphor rather than progress.
 *
 * ── Why these particular greens ─────────────────────────────────────────────
 *
 * The gradient stops (emerald-700 → emerald-800, resolved to sRGB in
 * `app/globals.css`) are deliberately dark. White label text on a CTA is
 * usually 13–15px — below the WCAG large-text cutoff — so it needs 4.5:1, and
 * emerald-600 only reaches 3.6:1. The 700/800 stops give 5.4:1 → 7.6:1 and
 * still hold 4.6:1 under the `hover:brightness-110` lift.
 *
 * Focus rings are solid emerald-600 rather than a translucent tint: the house
 * `ring-ring/50` measures 1.3–1.5:1 against a modal card and emerald-500 tops
 * out at 2.6:1 on white at any opacity — both under the 3:1 WCAG 2.2 minimum
 * for a non-text indicator. Solid emerald-600 clears it in both themes
 * (3.7:1 light / 5.0:1 dark).
 *
 * ── Composition ────────────────────────────────────────────────────────────
 *
 * These constants carry COLOUR AND STATE ONLY — never size, radius or layout.
 * Modals differ on shape (`rounded-full` pills in growth, `rounded-xl` in
 * billing) and that is intentional; keeping shape out means a caller can adopt
 * the palette without also adopting someone else's geometry:
 *
 *     className={cn("h-12 w-full rounded-xl text-[15px]", MODAL_CTA_SUCCESS)}
 *
 * Components that already render `<Button>` should use `variant="success"`
 * instead of `MODAL_CTA_SUCCESS` — the variant is the same treatment expressed
 * through the button system, and it also picks up the shared disabled/active
 * behaviour.
 */

/**
 * Primary CTA on a non-destructive modal, for hand-rolled `<button>` elements.
 *
 * Mirrors the `success` variant in `components/ui/button.tsx`: same gradient,
 * same brightness lift on hover, same solid focus ring. `disabled:opacity-60`
 * matches what the raw-button modals already used, so adopting this does not
 * change how a pending CTA looks — only its hue.
 */
export const MODAL_CTA_SUCCESS =
  "bg-panda-gradient-success text-white shadow-sm transition-all hover:brightness-110 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-background " +
  "disabled:cursor-not-allowed disabled:opacity-60";

/**
 * The same CTA where the loading label is status the user needs to READ
 * ("Redirecting to checkout…", "Activating…").
 *
 * `disabled:opacity-60` would drop that label to ~2.6:1, under the 4.5:1
 * minimum, so a busy button keeps full opacity and says why with the cursor.
 * Pair with `aria-busy` — `disabled` still blocks the click.
 */
export const MODAL_CTA_SUCCESS_BUSY =
  "disabled:cursor-wait disabled:opacity-100";

/**
 * Ring on the emphasised plan/offer card inside a modal — the green
 * counterpart to `ring-[#FF5606]`.
 */
export const MODAL_ACCENT_RING = "ring-emerald-600";

/**
 * Small filled pill riding on a plan card ("7 days", "Most popular").
 * Gradient rather than a flat fill so it reads as part of the CTA family.
 */
export const MODAL_ACCENT_PILL = "bg-panda-gradient-success text-white";

/**
 * Icon chip above a modal headline — the round/rounded-square tinted badge.
 * emerald-700 on the light tint is 4.8:1; emerald-400 on the dark tint is
 * 7.1:1, so the glyph stays legible in both themes.
 */
export const MODAL_ACCENT_CHIP =
  "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400";

/**
 * A selected option/radio card in a modal (plan pickers, credit amounts,
 * survey answers). The unselected state stays neutral and is left to callers.
 */
export const MODAL_ACCENT_SELECTED =
  "border-emerald-600 bg-emerald-50/60 dark:border-emerald-500 dark:bg-emerald-500/10";

/**
 * Inline accent text and glyphs (sparkles, checkmarks, "recommended" labels).
 */
export const MODAL_ACCENT_TEXT = "text-emerald-700 dark:text-emerald-400";

/**
 * Focus treatment for inputs inside a non-destructive modal, where the border
 * used to turn brand-red on focus and read like a validation failure.
 */
export const MODAL_ACCENT_FOCUS =
  "focus:border-emerald-600/60 dark:focus:border-emerald-500/60";
